import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

const GLOBAL_CARD_LEFT_SHIFT_MD_ONLY = -50; // mdモードでのみカード群全体を左にシフトする値（中央表示）
const ARROW_BUTTON_VERTICAL_ADJUSTMENT_PX = 60; // ボタンを下に下げるピクセル数

// 各ディスプレイモードごとのカード設定
// ★★★ 変更点 1: carouselContainerHeightプロパティを追加 ★★★
// コンテナの高さを独立して管理できるようにします。
const CARD_LAYOUT_SETTINGS = {
  custom: {
    baseCardWidth: 380,
    baseCardMediaHeight: 330,
    contentHeightEstimate: 440,
    sideCardCenterOffset: 220,
    sideCardScale: 0.9,
    carouselContainerHeight: 820,
  },
  xs: {
    baseCardWidth: 300,
    baseCardMediaHeight: 260,
    contentHeightEstimate: 410,
    sideCardCenterOffset: 175,
    sideCardScale: 0.8,
    carouselContainerHeight: 720,
  },
  sm: {
    baseCardWidth: 400,
    baseCardMediaHeight: 420,
    contentHeightEstimate: 350,
    sideCardCenterOffset: 280,
    sideCardScale: 0.8,
    carouselContainerHeight: 820,
  },
  md: {
    baseCardWidth: 490,
    baseCardMediaHeight: 480,
    contentHeightEstimate: 380,
    sideCardCenterOffset: 480,
    sideCardScale: 0.8,
    carouselContainerHeight: 920, // ★のmdブレークポイント。見切れないように高さを直接指定(例: 920px)
  },
  lg: {
    baseCardWidth: 540,
    baseCardMediaHeight: 420,
    contentHeightEstimate: 360,
    sideCardCenterOffset: 350,
    sideCardScale: 0.8,
    carouselContainerHeight: 780, // 420 + 360
  },
};

const CAROUSEL_BUTTON_SETTINGS = {
  custom: { size: 50, iconFontSize: 18, visualGap: 20 },
  xs: { size: 36, iconFontSize: 14, visualGap: 15 },
  sm: { size: 44, iconFontSize: 16, visualGap: 20 },
  md: { size: 56, iconFontSize: 20, visualGap: 25 },
  lg: { size: 60, iconFontSize: 22, visualGap: 25 },
};

const CENTER_CARD_HORIZONTAL_OFFSET_MD = 53;

const MotionMemberCard = ({
  japaneseName,
  englishName,
  title,
  description,
  image,
  hoverImage,
  icon,
  iconLink,
  motionX,
  motionScale,
  motionOpacity,
  motionZIndex,
  cardConfig,
  isActuallyCenter,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { baseCardWidth, baseCardMediaHeight, contentHeightEstimate } =
    cardConfig;
  const cardHeight = baseCardMediaHeight + contentHeightEstimate;
  const scaledCardWidthOffset = (baseCardWidth * motionScale) / 2;

  const cardStyles = {
    width: "100%",
    height: "100%",
    textAlign: "center",
    flexShrink: 0,
    bgcolor: "white",
    boxShadow: isActuallyCenter
      ? "0px 20px 40px rgba(0,0,0,0.2)"
      : "0px 8px 20px rgba(0,0,0,0.1)",
    transition: "box-shadow 0.3s ease-in-out",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const cardMediaStyles = {
    objectFit: "cover",
    flexShrink: 0,
  };

  const cardContentStyles = {
    pt: { xs: 1.5, md: 2 },
    pb: { xs: 2, md: 3 },
    px: { xs: 2, md: 3 },
    flexGrow: 1,
    overflow: "hidden",
  };

  const nameContainerStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    mb: 0.25,
  };

  const japaneseNameStyles = {
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "1.1rem", md: "1.3rem" },
  };

  const iconContainerStyles = (isLink) => ({
    ml: 1,
    display: "inline-flex",
    alignItems: "center",
    "& img": {
      width: { xs: 18, md: 20 },
      height: { xs: 18, md: 20 },
      transition: "transform 0.3s ease",
      verticalAlign: "middle",
    },
    "&:hover img": isLink ? { transform: "scale(1.2)" } : {},
  });

  const englishNameStyles = {
    display: "block",
    mb: { xs: 1, md: 1.5 },
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "0.8rem", md: "0.9rem" },
  };

  const titleStyles = {
    mb: { xs: 1.5, md: 2 },
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "1rem", md: "1.1rem" },
  };

  const descriptionStyles = {
    textAlign: "left",
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "0.85rem", md: "0.9rem" },
    lineHeight: { xs: 1.6, md: 1.8 },
    whiteSpace: "pre-line",
  };

  return (
    <motion.div
      animate={{
        x: motionX - scaledCardWidthOffset,
        scale: motionScale,
        opacity: motionOpacity,
      }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{
        position: "absolute",
        left: "50%",
        zIndex: motionZIndex,
        width: baseCardWidth,
        height: cardHeight,
        cursor: onClick ? "pointer" : "default",
        pointerEvents: motionOpacity === 0 ? "none" : "auto",
      }}
      onClick={onClick}
    >
      <Card sx={cardStyles}>
        <Box
          sx={{
            position: "relative",
            height: baseCardMediaHeight,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardMedia
            component="img"
            image={image}
            alt={japaneseName}
            sx={{
              ...cardMediaStyles,
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              left: 0,
              opacity: isHovered ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
          <CardMedia
            component="img"
            image={hoverImage}
            alt={`${japaneseName} (hover)`}
            sx={{
              ...cardMediaStyles,
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              left: 0,
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        </Box>

        <CardContent sx={cardContentStyles}>
          <Box sx={nameContainerStyles}>
            <Typography variant="h6" sx={japaneseNameStyles}>
              {japaneseName}
            </Typography>
            {icon && (
              <Box
                component={iconLink ? "a" : "span"}
                href={iconLink || undefined}
                target={iconLink ? "_blank" : undefined}
                rel={iconLink ? "noopener noreferrer" : undefined}
                sx={iconContainerStyles(!!iconLink)}
              >
                <img src={icon} alt={`${japaneseName} social icon`} />
              </Box>
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={englishNameStyles}
          >
            {englishName}
          </Typography>
          <Typography variant="subtitle1" color="text.primary" sx={titleStyles}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={descriptionStyles}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

MotionMemberCard.propTypes = {
  japaneseName: PropTypes.string.isRequired,
  englishName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  hoverImage: PropTypes.string,
  icon: PropTypes.string,
  iconLink: PropTypes.string,
  motionX: PropTypes.number.isRequired,
  motionScale: PropTypes.number.isRequired,
  motionOpacity: PropTypes.number.isRequired,
  motionZIndex: PropTypes.number.isRequired,
  cardConfig: PropTypes.object.isRequired,
  isActuallyCenter: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
};

const initialMembersData = [
  {
    japaneseName: "滝田 亜希子",
    englishName: "Takita Akiko",
    title: "代表取締役",
    description:
      "YouProducet代表の滝田です。動画全般・印刷物・CI開発・イベント/キャンペーン企画など、ジャンルを問わないクリエイティブディレクションで横断的に顧客課題解決に向き合っています。\nおかげさまで優秀なメンバーに恵まれ、個ではなくチームとして総合的な提案が可能な体制となりました。\n「視座は高く、奥行きは広く、引き出しは多く」を心得に、これからも事業の傍ら、趣味の人つなぎを実践していきます。",
    image: "member/滝田_1.jpg",
    hoverImage: "member/滝田_2.jpg",
    icon: "member/Facebook.png",
    iconLink: "https://www.facebook.com/akiko.miyao",
  },
  {
    japaneseName: "横川 寛人",
    englishName: "Yokokawa Hiroto",
    title: "監督・映像グループ統括",
    description:
      "映像編集、撮影を通じて、お客様の思いや魅力を伝えるサポートをしています。完成した映像を喜んでいただけた経験が、仕事の原動力になっています。\n休日は趣味の映画鑑KO鑑賞を通して映像表現を学び、日々の編集や撮影に活かしています。",
    image: "member/横川_1.jpg",
    hoverImage: "member/横川_2.jpg",
    icon: "member/x.png",
    iconLink: "https://x.com/hiroto3y",
  },
  {
    japaneseName: "峯村 優星",
    englishName: "Minemura Yusei",
    title: "編集担当",
    description:
      "映像編集を通して、お客様の想いや魅力を分かりやすく伝えることを大切にしています。視聴者の目線を意識し、最後まで見てもらえる動画づくりを心がけています。日々新しい表現を学びながら、より良い映像制作に取り組んでいます。",
    image: "member/Minemura.jpg",
    hoverImage: "member/Minemura.jpg",
    icon: "",
    iconLink: "",
  },
  {
    japaneseName: "渡井 直哉",
    hidden: true,
    englishName: "Watai Naoya",
    title: "営業・プロデューサー",
    description:
      "映像制作プロデューサーとして、BtoC向けのプロモーション動画や番組制作に携わっています。\n企画やキャスティング、ブッキングまで幅広くサポートし、見る人に届くコンテンツづくりを大切にしています。\n旅行で行ったタイにまつわる企画についても現在制作しており、今後も世界各国の文化や習慣などを動画を通じてお伝えします。\n趣味は海外旅行で、行った先の文化や習慣を知ることができるので、仕事にも活かしています。",
    image: "member/渡井_1.jpg",
    hoverImage: "member/渡井_2.jpg",
    icon: "member/x.png",
    iconLink: "https://x.com/WN0123386988971",
  },
  {
    japaneseName: "長澤 さくら",
    englishName: "Nagasawa Sakura",
    title: "Webマーケティンググループ",
    description:
      "#シゴトズキ【フジテレビ公式】というYouTube番組の中の人。\nSNSアカウントの運用やYouTubeショート、タイトル作成などを通して番組コミュニティを盛り上げています。\n「シゴトの好きをスキルに」をコンセプトに、見てくださる方々がロールモデルを見つけられるようなコンテンツを届けられるよう、日々発信しています。\n\"知りたい人に届けるマーケティング\"が得意です。",
    image: "member/長澤_1.jpg",
    hoverImage: "member/長澤_2.jpg",
    icon: "",
    iconLink: "",
  },
].filter((member) => !member.hidden);

const getCardMotionProps = (
  memberOriginalIndex,
  currentCenterIndex,
  totalMembers,
  displayMode,
  cardLayoutConfig,
) => {
  const { sideCardCenterOffset, sideCardScale } = cardLayoutConfig;

  let relativePosition =
    (memberOriginalIndex - currentCenterIndex + totalMembers) % totalMembers;
  if (relativePosition > totalMembers / 2) {
    relativePosition -= totalMembers;
  }

  let motionX, motionScale, motionOpacity, motionZIndex;
  let isActuallyCenter = false;

  const globalLeftShift =
    displayMode === "md" ? GLOBAL_CARD_LEFT_SHIFT_MD_ONLY : 0;
  const centerCardOffset =
    displayMode === "md" ? CENTER_CARD_HORIZONTAL_OFFSET_MD : 0;

  if (displayMode === "md" || displayMode === "custom") {
    // 3枚表示モード
    if (relativePosition === 0) {
      motionX = centerCardOffset + globalLeftShift;
      motionScale = 1;
      motionOpacity = 1;
      motionZIndex = 3;
      isActuallyCenter = true;
    } else if (
      relativePosition === -1 ||
      relativePosition === totalMembers - 1
    ) {
      motionX = -sideCardCenterOffset + globalLeftShift;
      motionScale = sideCardScale;
      motionOpacity = 1;
      motionZIndex = 1;
    } else if (
      relativePosition === 1 ||
      relativePosition === -(totalMembers - 1)
    ) {
      motionX = sideCardCenterOffset + globalLeftShift;
      motionScale = sideCardScale;
      motionOpacity = 1;
      motionZIndex = 1;
    } else {
      motionX =
        sideCardCenterOffset * relativePosition * 0.75 +
        centerCardOffset +
        globalLeftShift;
      motionScale = sideCardScale * 0.9;
      motionOpacity = 0;
      motionZIndex = 0;
    }
  } else {
    // 1枚表示モード
    if (relativePosition === 0) {
      motionX = 0;
      motionScale = 1;
      motionOpacity = 1;
      motionZIndex = 3;
      isActuallyCenter = true;
    } else {
      motionX = sideCardCenterOffset * relativePosition;
      motionScale = sideCardScale;
      motionOpacity = 0;
      motionZIndex = 0;
    }
  }

  return {
    motionX,
    motionScale,
    motionOpacity,
    motionZIndex,
    isActuallyCenter,
  };
};

const MembersSection = () => {
  const [centerIndex, setCenterIndex] = useState(0);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));

  const displayMode = useMemo(() => {
    if (isLgUp) return "md";
    if (isMdUp) return "custom";
    if (isSm) return "sm";
    return "xs";
  }, [isLgUp, isMdUp, isSm]);

  const currentCardLayoutConfig = CARD_LAYOUT_SETTINGS[displayMode];
  const currentButtonConfig = CAROUSEL_BUTTON_SETTINGS[displayMode];

  const moveSlide = (direction) => {
    setCenterIndex(
      (prevIndex) =>
        (prevIndex + direction + initialMembersData.length) %
        initialMembersData.length,
    );
  };

  const { carouselContainerHeight } = currentCardLayoutConfig;

  const sectionContainerStyles = { py: { xs: 0, md: 0 } };
  const mainContainerStyles = { textAlign: "center", position: "relative" };
  const titleBackgroundTextStyles = {
    position: "absolute",
    top: { xs: "15%", sm: "-10%", md: "-7%" },
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: { xs: "64px", sm: "8rem", md: "11rem" },
    fontFamily: "Roboto, sans-serif",
    fontWeight: "bold",
    color: "rgba(230, 0, 18, 0.05)",
    zIndex: 0,
    whiteSpace: "nowrap",
    width: "100%",
    userSelect: "none",
    pointerEvents: "none",
  };
  const titleHeaderStyles = {
    color: "#E60012",
    mb: 1.5,
    fontWeight: "bold",
    fontFamily: "Roboto, sans-serif",
    fontSize: { xs: "16px", md: "2.5rem" },
  };
  const titleDividerStyles = {
    height: 2,
    width: "6%",
    minWidth: "60px",
    bgcolor: "#1D2D5C",
    mx: "auto",
  };
  const carouselWrapperStyles = {
    position: "relative",
    mt: { xs: 4, md: 6 },
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: carouselContainerHeight,
    px: { xs: 1, sm: 2 },
  };
  const cardsMotionContainerStyles = {
    position: "relative",
    width: "100%",
    height: `${carouselContainerHeight}px`,
    overflow: "hidden",
  };

  const arrowButtonBaseStyles = {
    position: "absolute",
    top: `calc(${
      currentCardLayoutConfig.baseCardMediaHeight / 2
    }px + ${ARROW_BUTTON_VERTICAL_ADJUSTMENT_PX}px)`,
    transform: "translate(-50%, -50%)",
    bgcolor: "#fff",
    border: "1px solid #E60012",
    color: "#E60012",
    width: currentButtonConfig.size,
    height: currentButtonConfig.size,
    zIndex: 10,
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
    "&:hover": {
      bgcolor: "rgba(230,0,18,0.05)",
      transform: "translate(-50%, -50%) scale(1.05)",
    },
    transition: "all 0.2s ease-out",
  };

  const isThreeCardView = displayMode === "md" || displayMode === "custom";

  let leftButtonSx = {};
  let rightButtonSx = {};

  if (isThreeCardView) {
    const { baseCardWidth, sideCardCenterOffset, sideCardScale } =
      currentCardLayoutConfig;

    const globalShift =
      displayMode === "md" ? GLOBAL_CARD_LEFT_SHIFT_MD_ONLY : 0;
    const centerCardShift =
      displayMode === "md" ? CENTER_CARD_HORIZONTAL_OFFSET_MD : 0;

    const leftButtonAdjustment = displayMode === "md" ? 50 : -60;
    const rightButtonAdjustment = displayMode === "md" ? 0 : 60;

    const centerCardRightEdge = centerCardShift + baseCardWidth / 2;
    const rightSideCardLeftEdge =
      sideCardCenterOffset - (baseCardWidth * sideCardScale) / 2;
    const rightMidPointOffset =
      (centerCardRightEdge + rightSideCardLeftEdge) / 2 + rightButtonAdjustment;

    const centerCardLeftEdge = centerCardShift - baseCardWidth / 2;
    const leftSideCardRightEdge =
      -sideCardCenterOffset + (baseCardWidth * sideCardScale) / 2;
    const leftMidPointOffset =
      (centerCardLeftEdge + leftSideCardRightEdge) / 2 + leftButtonAdjustment;

    rightButtonSx = {
      left: `calc(50% + ${rightMidPointOffset}px + ${globalShift}px)`,
    };
    leftButtonSx = {
      left: `calc(50% + ${leftMidPointOffset}px + ${globalShift}px)`,
    };
  } else {
    const { baseCardWidth } = currentCardLayoutConfig;
    const { visualGap } = currentButtonConfig;
    const offset = baseCardWidth / 2 + visualGap;

    leftButtonSx = { left: `calc(50% - ${offset}px)` };
    rightButtonSx = { left: `calc(50% + ${offset}px)` };
  }

  return (
    <Box
      id="member"
      sx={{
        ...sectionContainerStyles,
        overflowX: "hidden", // 横スクロール防止
      }}
    >
      <Container maxWidth="xl" sx={mainContainerStyles}>
        <Box
          sx={{
            pt: { xs: 6, md: 10 },
            pb: { xs: 4, md: 6 },
            position: "relative",
          }}
        >
          <Typography
            variant="h1"
            aria-hidden="true"
            sx={{
              ...titleBackgroundTextStyles,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Member
          </Typography>
          <Box sx={{ zIndex: 1, position: "relative" }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{ ...titleHeaderStyles, fontFamily: "Roboto, sans-serif" }}
            >
              メンバー紹介
            </Typography>
            <Box sx={titleDividerStyles} />
          </Box>
        </Box>

        <Box sx={carouselWrapperStyles}>
          <IconButton
            onClick={() => moveSlide(-1)}
            sx={{ ...arrowButtonBaseStyles, ...leftButtonSx }}
            aria-label="前のメンバーへ"
          >
            <ArrowBackIosNewIcon
              sx={{ fontSize: currentButtonConfig.iconFontSize, ml: "2px" }}
            />
          </IconButton>

          <Box sx={cardsMotionContainerStyles}>
            {initialMembersData.map((member, index) => {
              const motionProps = getCardMotionProps(
                index,
                centerIndex,
                initialMembersData.length,
                displayMode,
                currentCardLayoutConfig,
              );

              const handleClick = () => {
                if (
                  isThreeCardView &&
                  !motionProps.isActuallyCenter &&
                  motionProps.motionOpacity > 0
                ) {
                  let relativePosition = index - centerIndex;
                  const total = initialMembersData.length;
                  if (Math.abs(relativePosition) > total / 2) {
                    if (relativePosition > 0) relativePosition -= total;
                    else relativePosition += total;
                  }

                  if (relativePosition === -1) {
                    moveSlide(-1);
                  } else if (relativePosition === 1) {
                    moveSlide(1);
                  }
                }
              };

              return (
                <MotionMemberCard
                  key={member.englishName}
                  {...member}
                  {...motionProps}
                  cardConfig={currentCardLayoutConfig}
                  onClick={handleClick}
                />
              );
            })}
          </Box>

          <IconButton
            onClick={() => moveSlide(1)}
            sx={{ ...arrowButtonBaseStyles, ...rightButtonSx }}
            aria-label="次のメンバーへ"
          >
            <ArrowForwardIosIcon
              sx={{ fontSize: currentButtonConfig.iconFontSize }}
            />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};

export default MembersSection;
